
import { FieldPath } from 'react-hook-form'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import { NctsCountry, tardekConfig } from '../../../../../../config'
import { FieldRequirement } from '../../../../models/fieldRules'
import { NctsError } from '../../../../../../shared/ncts/types'
import { getValueByPath } from '../../../../../../shared/rhf/models/types'
import getDeclarationFieldRequirementsResolver from '../../fieldRequirements/registry'
import { isEmpty } from '../../../../../../shared/lib/isEmpty'
import R0112Validator, { R0112_ERROR_MESSAGE } from '../../R0112'
import { collectFieldPaths } from '../collectFieldPaths'
import validateDocumentFileTypes from '../documentFileTypes'
import { FieldSateValidationContext } from '../types'

const TRANSIT_REQUIRED_FIELDS: FieldPath<TransitOperationFormValues>[] = [
  'departureCustomsOffice',
]

const ENS_REQUIRED_FIELDS: FieldPath<TransitOperationFormValues>[] = [
  'departureCustomsOffice',
]

const validateFieldRequirements = (
  values: TransitOperationFormValues,
  fields: FieldPath<TransitOperationFormValues>[],
  getFieldRequirement: (path: string) => FieldRequirement,
  security: string,
) => {
  const errors: NctsError[] = []

  for (const path of fields) {
    const requirement = getFieldRequirement(path)
    const value = getValueByPath(values, path)
    if (requirement === 'required' && isEmpty(value)) {
      errors.push({
        scope: 'transitOperation',
        path,
        message: 'Field is required',
      })
    }

    if (R0112Validator.appliesTo(security)
      && path.endsWith('telephone')
      && typeof value === 'string' && !isEmpty(value)
      && !R0112Validator.isValidInternationalPhoneNumber(value)) {
      errors.push({
        scope: 'transitOperation',
        path,
        message: R0112_ERROR_MESSAGE,
        code: 'R0112',
      })
    }
  }

  return errors
}

const validateFormFieldStates = ({
  isEns,
  isDraft,
  formValues,
  getFieldRequirement,
  security,
  country,
}: {
  isEns: boolean
  isDraft: boolean

  formValues: TransitOperationFormValues
  getFieldRequirement: (path: string) => FieldRequirement
  security: string
  country: string
}) => {
  if (isDraft) {
    const draftFields = isEns ? ENS_REQUIRED_FIELDS : TRANSIT_REQUIRED_FIELDS
    return validateFieldRequirements(formValues, draftFields, getFieldRequirement, security)
  }

  const allFields = collectFieldPaths(formValues, {
    ignoreFields: new Set(['id', 'deleted']),
  }) as FieldPath<TransitOperationFormValues>[]

  return [
    ...validateFieldRequirements(formValues, allFields, getFieldRequirement, security),
    ...validateDocumentFileTypes(formValues, country, 'transitOperation'),
  ]
}

function validateTransitOperationFormFieldStates(
  formValues: TransitOperationFormValues,
  ctx: FieldSateValidationContext | undefined,
): NctsError[] {
  const country = ctx?.country ?? '' as NctsCountry
  const isDraft = ctx?.isDraft ?? true
  const isPhase6OptIn = tardekConfig.phase6OptInCountries.includes(country)
  const isPhase6OptOut = tardekConfig.phase6OptOutCountries.includes(country)
  const security = ctx?.security ?? '0'
  const phase = isPhase6OptIn || isPhase6OptOut ? 'p6' : 'p5'
  const additionalDeclarationType = ctx?.additionalDeclarationType ?? 'A'
  const specificCircumstanceIndicator = ctx?.specificCircumstanceIndicator ?? ''
  const isEns = ctx?.isEns ?? false

  const resolveRequirement = getDeclarationFieldRequirementsResolver('transitOperation', isEns)

  return validateFormFieldStates({
    isEns,
    isDraft,
    formValues,
    security,
    country,
    getFieldRequirement: (path: string) => resolveRequirement(path, {
      country,
      phase,
      security,
      valuesMap: formValues,
      isOptIn: isPhase6OptIn,
      additionalDeclarationType,
      specificCircumstanceIndicator,
      serverValidationResult: ctx?.serverValidationResult,
    }),
  })
}

export default validateTransitOperationFormFieldStates
